import React, { useEffect, useState } from "react";
import styles from "./ProboPrediction.module.scss";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

import Loader from "@components/Loader";
import { Theme } from "@/constants/enums";
import { formatITCTime, normalizeToPercentage } from "@utils/timeStamptoUTC";
import useWindowDimensions from "@utils/useWindowDimensions";

import PeopleIcon from "@public/widget/svg/group-of-people.svg";
import GroupIcon from "@public/widget/svg/grp-icon-grp.svg";

interface ProboPredictionGraphType {
    /*The question to be displayed */
    questions: string;

    /*Total no of user engaged to be displayed */
    totalTraders: string;

    xAxisParameter: any;
    /*The y axis data for yes*/
    yAxisDataForYes: any;

    /*The y axis data for yes*/
    yAxisDataForNo: any;

    /*Theme thats need to be done */
    theme?: Theme;
}

/**
 * ProboPredictionGraph component renders an interactive Highcharts graph with a toggle switch.
 * @param {ProboPredictionGraphType} props - Props passed to the component.
 * @returns {JSX.Element} The rendered component.
 */
const ProboPredictionGraph: React.FC<ProboPredictionGraphType> = (props) => {
    const {
        questions,
        totalTraders,
        xAxisParameter = [],
        yAxisDataForYes = [],
        yAxisDataForNo = [],
        theme = Theme.LIGHT,
    } = props;

    /**
     * State to store the current chart data.
     * @type {[any, Function]}
     */

    /**
     * Loading state for the chart.
     * @type {[boolean, Function]}
     */
    const [isLoading, setIsLoading] = useState<boolean>(true);

    /**
     * State to track whether "Yes" is selected.
     * @type {[boolean, Function]}
     */

    const [chartData, setChartData] = useState<any>(null);

    /**
     * Generates chart configuration for "Yes" responses.
     * @param {Array<number>} data - The data points for the "Yes" chart.
     * @returns {Object} The Highcharts configuration object.
     */
    const axisLabelColor = theme === Theme.DARK ? "#e0e0e0" : "#7A8190";

    const firstTimeStamp = xAxisParameter[0];
    const lastTimeStamp = xAxisParameter[xAxisParameter.length - 1];
    const durationInHours = (lastTimeStamp - firstTimeStamp) / (1000 * 60 * 60);
    const dimensions = useWindowDimensions();

    const formattedXAxisData = xAxisParameter.map((timestamp) => ({
        formattedLabel: formatITCTime(timestamp, firstTimeStamp, lastTimeStamp), // Display label
        originalTimestamp: timestamp, // Store raw timestamp for tooltip
    }));

    useEffect(() => {
        setChartData({
            chart: {
                type: "line",
                height: "160px",
                zoomType: "x",
                backgroundColor: theme === Theme.LIGHT ? "#ffffff" : "",
            },
            title: { text: undefined },
            xAxis: {
                categories: formattedXAxisData.map(
                    (item) => item.formattedLabel,
                ),
                lineWidth: 0,
                labels: {
                    y: 20,
                    style: {
                        fontFamily: "Lato",
                        fontSize: "10px",
                        color:
                            theme === Theme.DARK && dimensions?.width < 640
                                ? "#fff"
                                : "#7A8190",
                    },
                },
                // This draws a vertical line (crosshair) following the pointer
                crosshair: {
                    color: "#999",
                    width: 1,
                    dashStyle: "Solid",
                },
            },
            yAxis: [
                {
                    title: { text: null },
                    max: 110,
                    min: 0,
                    labels: { enabled: false },
                    gridLineColor: "transparent",
                    tickPositions: [0, 50, 100],
                    endOnTick: false, // This prevents auto-adjustment of ticks
                },
                {
                    title: { text: null },
                    max: 110,
                    min: 0,
                    gridLineColor: "transparent",
                    tickPositions: [0, 50, 100],
                    endOnTick: false, // This prevents auto-adjustment of ticks

                    labels: {
                        formatter: function () {
                            // @ts-ignore
                            return `${this.value}%`;
                        },
                        x: -15,
                        style: {
                            fontFamily: "Lato",
                            fontSize: "10px",
                            color:
                                theme === Theme.DARK && dimensions?.width < 640
                                    ? "#fff"
                                    : "#7A8190",
                        },
                    },
                },
            ],
            legend: { enabled: false },
            series: [
                {
                    type: "line",
                    name: "Yes",
                    data: normalizeToPercentage(yAxisDataForYes),
                    lineWidth: 1,
                    lineColor: "#059669",
                    color: {
                        linearGradient: { x1: 0, y1: -0.23, x2: 0, y2: 0.93 },
                        stops: [
                            [0, "rgba(205, 255, 97, 0.44)"],
                            [0.5, "rgba(205, 255, 97, 0.17)"],
                            [1, "rgba(205, 255, 97, 0.00)"],
                        ],
                    },
                    marker: { enabled: false },
                    yAxis: 0,
                },
                {
                    type: "line",
                    name: "No",
                    data: normalizeToPercentage(yAxisDataForNo),
                    lineWidth: 1,
                    lineColor: "#F43F5E",
                    color: {
                        linearGradient: { x1: 0, y1: -0.23, x2: 0, y2: 0.93 },
                        stops: [
                            [0, "rgba(251, 113, 133, 0.44)"],
                            [0.5, "rgba(251, 113, 133, 0.17)"],
                            [1, "rgba(251, 113, 133, 0.00)"],
                        ],
                    },
                    marker: { enabled: false },
                    yAxis: 1,
                },
            ],
            tooltip: {
                shared: true,
                useHTML: true,
                outside: true,

                formatter: function () {
                    // @ts-ignore
                    const pointIndex = this.points[0].point.index;
                    const originalTimestamp =
                        formattedXAxisData[pointIndex].originalTimestamp;
                    const dateObj = new Date(originalTimestamp);
                    const textColor =
                        theme === Theme.DARK ? "#ffffff" : "#010101";

                    const formattedTime = dateObj.toLocaleString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                        timeZone: "Asia/Kolkata",
                    });

                    // @ts-ignore
                    const yesPoint = this.points?.find(
                        (p) => p.series.name === "Yes",
                    );
                    // @ts-ignore
                    const noPoint = this.points?.find(
                        (p) => p.series.name === "No",
                    );

                    // Ensure values are numbers before comparison
                    const yesPercentage = yesPoint
                        ? Number(yesPoint.y.toFixed(0))
                        : 0;
                    const noPercentage = noPoint
                        ? Number(noPoint.y.toFixed(0))
                        : 0;

                    const isYesHigher = yesPercentage > noPercentage;
                    return `<div style="text-align: center; position: relative;">
                    
                     <div style="
            position: absolute; 
            right: -10px; 
            top: -40px;
            padding: 4px; 
            background-color: #fff;
             color: ${isYesHigher ? "green" : "red"}; 
            border: 1px solid #F3F4F6;
            box-shadow: 0px 1px 2px 0px #0000000D;
            border-radius: 4px;
        ">
           <b>${
               isYesHigher ? `Yes: ${yesPercentage}%` : `No: ${noPercentage}%`
           }</b>
        </div>
        
        <!-- No Percentage Box -->
        <div style="
            position: absolute; 
            right: -10px; 
            bottom: -60px;
            padding: 4px; 
            background-color: #fff;
             color: ${isYesHigher ? "red" : "green"}; 
            border: 1px solid #F3F4F6;
            box-shadow: 0px 1px 2px 0px #0000000D;
            border-radius: 4px;
        ">
           <b>${
               isYesHigher ? `No: ${noPercentage}%` : `Yes: ${yesPercentage}%`
           }</b>
        </div>
                    <div style="position: absolute; right: -80px; top: 5px; z-index: 10000;color: ${textColor}"><b>${formattedTime}</b></div>
                </div>`;
                },
                backgroundColor: "",
                style: {
                    color: "#010101",
                    fontSize: "12px",
                    fontFamily: "Lato",
                    fontWeight: 800,
                    zIndex: 10000,
                },
            },
        });
        setIsLoading(false);
    }, [yAxisDataForYes, yAxisDataForNo]);

    return (
        <section className={styles.card} data-theme={theme}>
            {isLoading ? (
                <div className={styles["loader-container"]}>
                    <Loader />
                </div>
            ) : (
                <>
                    <div className={styles.header} data-theme={theme}>
                        <div>{questions}</div>
                    </div>
                    <main className={styles.ChartWrapper}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={chartData}
                        />
                    </main>
                    <div className={styles["total-trader"]} data-theme={theme}>
                        {theme === Theme.LIGHT ? <PeopleIcon /> : <GroupIcon />}
                        {totalTraders} Users
                    </div>
                </>
            )}
        </section>
    );
};

export default ProboPredictionGraph;
